import {
  AppLayout,
  Box,
  Default_Text_Color,
  Typography,
  Avatar,
  IconButton,
  TextField,
  Divider,
  CustomButton,
  EditIcon,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ExpandMoreIcon,
  Grid2,
  useApi,
  Toaster,
  Select,
  MenuItem,
  CircularProgress, // Add this import
} from "../utils/globalImports";
import { useContext, useEffect, useRef, useState } from "react";
import { CameraAlt, WindowSharp } from "@mui/icons-material";
import { AuthContext } from "../utils/authContext";
import { User } from "../constants/types";


const faqs = [
  {
    question: "How can I change my password?",
    answer: 'Click the "Change Password" button in the Security section to update your password.',
  },
  {
    question: "How do I update my profile picture?",
    answer: "Click the camera icon on your avatar to upload a new profile picture.",
  },
  {
    question: "Can I change my email address?",
    answer: "Email address changes are currently not allowed through the UI. Please contact support for assistance.",
  },
];


const Profile: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [isEditable, setIsEditable] = useState(false);
    const { user, setUser } = useContext(AuthContext);
    const { execute, loading, error } = useApi();
    const [roles, setRoles] = useState<{ roleId: number; roleName: string }[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [showPasswordReset, setShowPasswordReset] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [openToaster, setOpenToaster] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [isErrorToast, setIsErrorToast] = useState(false);
    const [isPasswordLoading, setIsPasswordLoading] = useState(false); // Add loading state for password reset
      const [formData, setFormData] = useState({
          firstName: user?.firstName || '',
          lastName: user?.lastName || '',
          email: user?.email || '',
          role: user?.role || '',
        });
    const originalRoleRef = useRef(user?.role || '');
        

  useEffect(() => {
  const fetchRoles = async () => {
    const { data, error } = await execute<{ roleId: number; roleName: string }[]>('get', '/user/GetAllRoles');
    if (!error && data) {
      setRoles(data);
    } else {
      setToastMessage("Failed to fetch roles");
      setIsErrorToast(true);
      setOpenToaster(true);
    }
  };

  fetchRoles();
}, []);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const firstName = user?.firstName;;
  const lastName = user?.lastName;
  const Email = user?.email;
  const fullName = `${firstName} ${lastName}`;
  const initials = `${firstName?.charAt(0)}${lastName?.charAt(0)}`.toUpperCase();
  const role = user?.role;

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const fileReader = new FileReader();
      fileReader.onload = () => {
        if (typeof fileReader.result === "string") {
          setProfileImage(fileReader.result);
        }
      };
      fileReader.readAsDataURL(event.target.files[0]);
    }
  };

  const handleVerifyPassword = async () => {
    if (!currentPassword) {
      setToastMessage("Please enter your current password.");
      setIsErrorToast(true);
      setOpenToaster(true);
      return;
    }

    setIsPasswordLoading(true); // Start loading

    try {
      const payload = {
        userId: user?.userId,
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user?.email || '',
        password: currentPassword, // sending entered password
        roleId: 0,
        status: 1,
        updatedId: user?.userId,
        roleName: user?.role,
      };

      const { data, error } = await execute('post', '/auth/verify-password', payload);

      if (error) {
        setToastMessage("Incorrect password.");
        setIsErrorToast(true);
      } else {
        setToastMessage("An Email is sent to your registered email address to reset your password.");
        // Redirect to reset password page
        localStorage.setItem('resetPasswordFlag', 'true');
        setIsErrorToast(false);
        setCurrentPassword(''); // Clear password field on success
        setShowPasswordReset(false); // Hide password reset form
      }

      setOpenToaster(true);
    } catch (err) {
      setToastMessage("An error occurred. Please try again.");
      setIsErrorToast(true);
      setOpenToaster(true);
    } finally {
      setIsPasswordLoading(false); // Stop loading
    }
  }

  const handleSave = async () => {
  const selectedRole = roles.find((r) => r.roleName === formData.role);

  const payload = {
    userId: user?.userId,
    firstName: formData.firstName,
    lastName: formData.lastName,
    email: formData.email,
    roleId: selectedRole?.roleId || null,
    password: '', // Ensure it's provided securely
    updatedId: user?.userId,
    updatedAt: new Date().toISOString(),
    status: 1,
    roleName: formData.role,
  };

  const { data, error } = await execute<null>('put', '/user/UpdateUser', payload);

  if (error) {
    setToastMessage(`Update failed: ${error}`);
    setIsErrorToast(true);
    setOpenToaster(true);
  } else {
    const roleChanged = formData.role !== originalRoleRef.current;

    if (roleChanged) {
      setToastMessage("Role updated. You will be logged out in 5 seconds.");
      setIsErrorToast(false);
      setOpenToaster(true);

      setTimeout(async () => {
        await execute('post', '/auth/logout', {});
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = '/'; // Redirect to login page
      }, 5000);
    } else {
      setToastMessage("Profile updated successfully");
      setIsErrorToast(false);
      setOpenToaster(true);

       setUser((prevUser) => ({
           ...prevUser!,
          firstName: formData.firstName,
          lastName: formData.lastName,
        }));

        localStorage.setItem('name', `${formData.firstName} ${formData.lastName}`);
    }

    setIsEditable(false);
  }
};



  return (
    <AppLayout>
       <Box
            sx={{
            height: '100vh',
            overflowY: 'auto',
            padding: 2,
            }}
        >
        <Typography
          variant="h4"
          color={Default_Text_Color}
          fontWeight={500}
          fontSize={40}
          mb={3}
        >
          Profile Page
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Box
          sx={{
            display: "flex",
            gap: 4,
            flexWrap: "wrap",
          }}
        >
          {/* Left Box */}
          <Box
            sx={{
              width: 300,
              p: 3,
              border: "1px solid #ddd",
              borderRadius: 2,
              boxShadow: 1,
              textAlign: "center",
              backgroundColor: "#fff",
            }}
          >
            <Box sx={{ position: "relative", width: 160, height: 160, margin: "0 auto" }}>
              <Avatar
                sx={{
                  width: 160,
                  height: 160,
                  fontSize: 48,
                  bgcolor: Default_Text_Color,
                }}
                src={profileImage || undefined}
              >
                {!profileImage && initials}
              </Avatar>

              <IconButton
                sx={{
                  position: "absolute",
                  bottom: 8,
                  right: 8,
                  backgroundColor: "#fff",
                  border: "1px solid #ccc",
                  width: 40,
                  height: 40,
                  "&:hover": {
                    backgroundColor: "#f0f0f0",
                  },
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <CameraAlt fontSize="small" />
              </IconButton>

              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleImageChange}
              />
            </Box>

            <Typography variant="h6" mt={2} fontWeight={600}>
              {fullName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {role}
            </Typography>
          </Box>

          {/* Right Box */}
        <Box
            sx={{
                flex: 1,
                p: 3,
                border: "1px solid #ddd",
                borderRadius: 2,
                boxShadow: 1,
                backgroundColor: "#fff",
                minWidth: 300,
                position: "relative",
                transition: "all 0.3s ease", // Smooth size changes
                ...(isEditable && {
                p: 4, // slightly more padding in edit mode
                }),
            }}
            >

        {/* Edit Button */}
        <IconButton
            onClick={() => setIsEditable((prev) => !prev)}
            sx={{ position: "absolute", top: 16, right: 16 }}
            aria-label="edit"
        >
            <EditIcon />
        </IconButton>

        <Typography variant="h6" fontWeight={600} mb={2}>
            User Details
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
           <Grid2 container spacing={2} justifyContent="space-between">
            <Grid2 sx={{ width: "49%" }}>
             <TextField
                  label="First Name"
                  value={formData.firstName}
                  onChange={(e) => handleChange("firstName", e.target.value)}
                  fullWidth
                  disabled={!isEditable}
                />
            </Grid2>

            <Grid2 sx={{ width: "49%" }}>
              <TextField
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => handleChange("lastName", e.target.value)}
                fullWidth
                disabled={!isEditable}
              />
            </Grid2>
          </Grid2>

           <TextField
              label="Email"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              fullWidth
              disabled={true}
            />
              <TextField
                  select
                  label="Role"
                  value={formData.role}
                  onChange={(e) => handleChange("role", e.target.value)}
                  fullWidth
                  disabled={role !== "Admin" || !isEditable}
                >
                  <MenuItem value="" disabled>Select a role</MenuItem>
                  {roles.map((r) => (
                    <MenuItem key={r.roleId} value={r.roleName}>
                      {r.roleName}
                    </MenuItem>
                  ))}
                </TextField>
            </Box>

            {isEditable && (
                <Box
                sx={{
                    mt: 3,
                    opacity: isEditable ? 1 : 0,
                    maxHeight: isEditable ? 100 : 0,
                    transform: isEditable ? "translateY(0)" : "translateY(-10px)",
                    transition: "all 0.3s ease",
                    overflow: "hidden",
                    width: "25%"
                }}
                >
                <CustomButton onClick={handleSave}>Save</CustomButton>
                </Box>
            )}
            </Box>

          <Divider sx={{ width: "100%", mt: 4 }} />
          <Box  sx={{ width: "100%", mt: 4 }}>
            <Typography
              variant="h6"
              fontWeight={600}
              mt={2}
              mb={2}
              color={Default_Text_Color}
            >
              Security
            </Typography>
           <CustomButton 
            sx={{ width: { md: '20%', sm: '50%' } }}
             onClick={() => setShowPasswordReset((prev) => !prev)}
             disabled={isPasswordLoading} // Disable when loading

           >
            {showPasswordReset ? "Cancel" : "Change Password"}
          </CustomButton>
          {showPasswordReset && (
            <Box mt={2} display="flex" flexDirection="column" gap={2} width="50%">
              <TextField
                type="password"
                label="Current Password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                fullWidth
                disabled={isPasswordLoading} // Disable input when loading
              />
              <CustomButton
                sx={{ 
                  width: { md: '40%', sm: '70%' },
                }}
                onClick={handleVerifyPassword}
                disabled={isPasswordLoading || !currentPassword.trim()} // Disable when loading or empty password
              >
                {isPasswordLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Verify Password"
                )}
              </CustomButton>
            </Box>
)}

          </Box>
          <Divider sx={{ width: "100%", mt: 4 }} />
          <Box sx={{ width: "100%", mb: 7 }}>
                <Typography
                    variant="h6"
                    fontWeight={600}
                    mt={2}
                    mb={2}
                    color={Default_Text_Color}
                >
                    Support & Help
                </Typography>

                <Typography variant="body2" mb={1}>
                    For further assistance, please contact support or review the FAQs below.
                </Typography>

                {faqs.map((faq, index) => (
                    <Accordion key={index}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography fontWeight={500}>{faq.question}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography>{faq.answer}</Typography>
                    </AccordionDetails>
                    </Accordion>
                ))}
                </Box>


        </Box>
      </Box>
          <Toaster
            open={openToaster}
            onClose={() => setOpenToaster(false)}
            message={toastMessage}
            type={isErrorToast ? "error" : "success"}
            duration={3000}
          />
    </AppLayout>
  );
};

export default Profile;